import { TelSysTypes } from '../Constants/Constants'

export type WebexAuthorisationFieldsDirty = {
    userName1?: boolean
    password1?: boolean
    userName3?: boolean
}

export type CallRecordingAuthorisationState = {
    telSysType: TelSysTypes
    callRecordingEnabled: boolean
    savedCallRecordingAuthorised: boolean
    isTenantAuthenticated: boolean
    authorisedCallRecordingEnabled: Nullable<boolean>
}

/**
 * Returns whether call recording is on for a Webex Calling server whose Webex authorisation was granted without it,
 * so no recordings can be collected until it is authorised again.
 *
 * The recordings scope is only requested when call recording is on. An authorisation made in the form takes the setting
 * it was granted under; without one, the server's saved authorisation stands, which the server records whenever a new
 * authorisation is saved. Turning call recording off needs no new authorisation, because a token that can still read
 * recordings does no harm. This does not block saving: the server can also be authorised again from the Auth Email,
 * which needs call recording to be saved on first.
 *
 * @param state The form's type, call recording setting and authorisation state.
 * @returns True when the server must be authorised again for call recordings to be collected.
 */
export const isCallRecordingReauthorisationNeeded = ({
    telSysType,
    callRecordingEnabled,
    savedCallRecordingAuthorised,
    isTenantAuthenticated,
    authorisedCallRecordingEnabled
}: CallRecordingAuthorisationState): boolean => {
    const hasAuthorisation: boolean = isTenantAuthenticated || authorisedCallRecordingEnabled !== null

    const grantedCallRecordingEnabled: boolean = authorisedCallRecordingEnabled ?? savedCallRecordingAuthorised

    return (
        telSysType === TelSysTypes.WEBEX_CALLING &&
        hasAuthorisation &&
        callRecordingEnabled &&
        !grantedCallRecordingEnabled
    )
}

/**
 * Returns whether call recording was turned on after the admin authorised a Webex Calling server in the form without
 * it, which blocks saving.
 *
 * The admin has just authorised in the form, so they can authorise again there; an authorisation the server already
 * holds never blocks saving (see {@link isCallRecordingReauthorisationNeeded}). Callers skip this while a change the
 * authorisation depends on is unsaved (see {@link isWebexAuthorisationChangeUnsaved}), because the authorise button is
 * hidden until the server is saved.
 *
 * @param state The form's type, call recording setting and authorisation state.
 * @returns True when the server must be authorised again in the form before it is saved.
 */
export const isFormAuthorisationWithoutCallRecording = ({
    telSysType,
    callRecordingEnabled,
    authorisedCallRecordingEnabled
}: CallRecordingAuthorisationState): boolean =>
    telSysType === TelSysTypes.WEBEX_CALLING && callRecordingEnabled && authorisedCallRecordingEnabled === false

/**
 * Returns whether the Client ID or Client Secret of a Webex server are being edited, which hides the authorise button
 * until the server is saved.
 *
 * A changed Client ID leaves the existing token unusable, because Webex ties its refresh token to the client ID. A
 * changed Client Secret does not, but the authorise button can only send the saved Client Secret, so the admin cannot
 * authorise with it until it is saved. The Auth Email is not read by the authorise button, so editing it is left out.
 *
 * @param dirtyCommunications The communications fields the admin has edited.
 * @returns True when the Client ID or Client Secret are being edited.
 */
export const areWebexCredentialsDirty = (dirtyCommunications: Definable<WebexAuthorisationFieldsDirty>): boolean =>
    !!dirtyCommunications?.userName1 || !!dirtyCommunications?.password1

/**
 * Returns whether a change the Webex authorisation depends on is unsaved, which hides the authorise button until the
 * server is saved.
 *
 * Besides the Client ID and Client Secret (see {@link areWebexCredentialsDirty}), the call recording setting decides the
 * scope Webex is asked for. Authorising under an unsaved setting would leave Webex holding a grant that does not match
 * the saved setting, which the Auth Email and the next authorisation request the scope from. The setting is compared with
 * the saved one rather than read from the form's dirty fields, because the checkbox does not mark itself as edited.
 * It is only compared for Webex Calling: the checkbox keeps its value when the type is changed, and no other type's
 * authorisation depends on it. A new server has no saved setting a grant could drift from, so it can be authorised and
 * created in one go under whatever setting the form holds.
 *
 * @param telSysType The type chosen in the form.
 * @param dirtyCommunications The communications fields the admin has edited.
 * @param callRecordingEnabled Whether call recording is on in the form.
 * @param savedCallRecordingEnabled Whether call recording is on for the saved server, undefined for a new one.
 * @returns True when the Client ID, Client Secret or, for Webex Calling, the call recording setting are being edited.
 */
export const isWebexAuthorisationChangeUnsaved = (
    telSysType: TelSysTypes,
    dirtyCommunications: Definable<WebexAuthorisationFieldsDirty>,
    callRecordingEnabled: boolean,
    savedCallRecordingEnabled: Definable<boolean>
): boolean =>
    areWebexCredentialsDirty(dirtyCommunications) ||
    (telSysType === TelSysTypes.WEBEX_CALLING &&
        savedCallRecordingEnabled !== undefined &&
        callRecordingEnabled !== savedCallRecordingEnabled)
